function sortKeysDeep(x: unknown): unknown {
  if (x === null || typeof x !== "object") return x;
  if (Array.isArray(x)) return x.map(sortKeysDeep);
  const o = x as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(o).sort()) {
    sorted[k] = sortKeysDeep(o[k]);
  }
  return sorted;
}
import {
  tipTapJsonToPlainText,
  tipTapJsonToRichParagraphs,
  tryParseStringifiedTipTapDoc,
  looksLikeTipTapJsonString,
  plainTextToRichParagraphs,
  type RichParagraph,
} from "./tipTapPlainText";
import { ensureParagraphIds, type ChapterBlockNode, type ChapterDocContent } from "./chapterParagraphs";

export type DocContent = ChapterDocContent;

/** Writing marks allowed in the manuscript editor (comment overlays stripped separately). */
const ALLOWED_WRITING_MARKS = new Set([
  "bold",
  "italic",
  "underline",
  "strong",
  "em",
  "properNoun",
  "citation",
]);

const BLOCK_TYPES_TO_PARAGRAPH = new Set([
  "heading",
  "blockquote",
  "codeBlock",
  "listItem",
]);

const LIST_BLOCK_TYPES = new Set(["bulletList", "orderedList"]);

export function hasAnyText(nodes: unknown[]): boolean {
  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    const n = node as { type?: string; content?: unknown[]; text?: string };
    if (n.type === "text" && typeof n.text === "string" && n.text.trim().length > 0) return true;
    if (n.type === "image") return true;
    if (Array.isArray(n.content) && hasAnyText(n.content)) return true;
  }
  return false;
}

function unwrapNestedTipTapDoc(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object") {
    const plain = tipTapJsonToPlainText(value).trim();
    if (looksLikeTipTapJsonString(plain)) return tryParseStringifiedTipTapDoc(plain);
    return value as Record<string, unknown>;
  }
  if (typeof value === "string") {
    const doc = tryParseStringifiedTipTapDoc(value);
    if (!doc) return null;
    const plain = tipTapJsonToPlainText(doc).trim();
    if (looksLikeTipTapJsonString(plain)) return tryParseStringifiedTipTapDoc(plain);
    return doc;
  }
  return null;
}

/** True when a string contains a serialized TipTap node object (never appears in normal prose). */
function containsSerializedTipTapNodes(s: string): boolean {
  return /\{\s*"type"\s*:\s*"(?:doc|paragraph|heading|blockquote|codeBlock|bulletList|orderedList|listItem|text|hardBreak)"/.test(
    s
  );
}

/** End index (exclusive) of the balanced {…}/[…] starting at `start`, or -1 if unbalanced/truncated. */
function balancedJsonEnd(s: string, start: number): number {
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i]!;
    if (inStr) {
      if (esc) esc = false;
      else if (ch === "\\") esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === "{" || ch === "[") depth++;
    else if (ch === "}" || ch === "]") {
      depth--;
      if (depth === 0) return i + 1;
      if (depth < 0) return -1;
    }
  }
  return -1;
}

/**
 * Pull serialized TipTap nodes out of a text blob, tolerating a bracket-less
 * comma-separated run (`{…},{…}`), a leading/trailing prose fragment, and
 * truncation. Returns the recovered nodes plus any clean leading prose, or null.
 */
function extractSerializedTipTapNodes(text: string): { leading: string; nodes: unknown[] } | null {
  const nodes: unknown[] = [];
  let firstStart = -1;
  let i = 0;
  while (i < text.length) {
    const ch = text[i]!;
    if (ch === "{" || ch === "[") {
      const end = balancedJsonEnd(text, i);
      if (end > i) {
        try {
          const parsed = JSON.parse(text.slice(i, end)) as unknown;
          const collected: unknown[] = [];
          if (Array.isArray(parsed)) {
            for (const x of parsed) {
              if (x && typeof x === "object" && typeof (x as { type?: unknown }).type === "string") collected.push(x);
            }
          } else if (parsed && typeof parsed === "object" && typeof (parsed as { type?: unknown }).type === "string") {
            const p = parsed as { type: string; content?: unknown[] };
            if (p.type === "doc" && Array.isArray(p.content)) collected.push(...p.content);
            else collected.push(p);
          }
          if (collected.length > 0) {
            if (firstStart < 0) firstStart = i;
            nodes.push(...collected);
            i = end;
            while (i < text.length && (text[i] === "," || /\s/.test(text[i]!))) i++;
            continue;
          }
        } catch {
          /* not valid JSON at this position — fall through */
        }
      }
    }
    i++;
  }
  if (nodes.length === 0) return null;
  const leadingRaw = firstStart > 0 ? text.slice(0, firstStart) : "";
  const leading =
    /[{}[\]]/.test(leadingRaw) || containsSerializedTipTapNodes(leadingRaw) ? "" : leadingRaw.trim();
  return { leading, nodes };
}

/** Group loose inline nodes into paragraphs; keep block nodes as-is. */
function coerceToBlockNodes(nodes: unknown[]): unknown[] {
  const out: unknown[] = [];
  let inline: unknown[] = [];
  const flush = () => {
    if (inline.length > 0) {
      out.push({ type: "paragraph", attrs: { id: "" }, content: inline });
      inline = [];
    }
  };
  for (const nd of nodes) {
    if (!nd || typeof nd !== "object") continue;
    const t = (nd as { type?: string }).type;
    if (t === "text" || t === "hardBreak") inline.push(nd);
    else {
      flush();
      out.push(nd);
    }
  }
  flush();
  return out;
}

/**
 * Recover individual blocks whose text is itself stringified TipTap JSON.
 * `unwrapNestedTipTapDoc` only handles the case where the *whole* doc is
 * double-encoded; a bad merge/sync can instead poison only some blocks, leaving
 * the rest as real prose (so the chapter renders normally until it hits a block
 * whose text is a raw `{"type":"paragraph",...}` blob — sometimes bracket-less,
 * mixed with prose, or truncated). This splices the recovered nodes back in place
 * so the reader shows prose, not JSON.
 */
function recoverNestedJsonBlocks(nodes: unknown[], depth = 0): unknown[] {
  if (depth > 6) return nodes;
  const out: unknown[] = [];
  for (const node of nodes) {
    if (node && typeof node === "object") {
      const plain = tipTapJsonToPlainText(node).trim();
      if (containsSerializedTipTapNodes(plain)) {
        let recovered: unknown[] | null = null;
        let leading = "";
        const wholeDoc = tryParseStringifiedTipTapDoc(plain);
        const wholeContent =
          wholeDoc && Array.isArray((wholeDoc as { content?: unknown[] }).content)
            ? ((wholeDoc as { content?: unknown[] }).content as unknown[])
            : null;
        if (wholeContent && wholeContent.length > 0) {
          recovered = wholeContent;
        } else {
          const ex = extractSerializedTipTapNodes(plain);
          if (ex) {
            recovered = ex.nodes;
            leading = ex.leading;
          }
        }
        if (recovered && recovered.length > 0) {
          const rebuilt: unknown[] = [];
          if (leading) {
            rebuilt.push({ type: "paragraph", attrs: { id: "" }, content: [{ type: "text", text: leading }] });
          }
          rebuilt.push(...coerceToBlockNodes(recovered));
          out.push(...recoverNestedJsonBlocks(rebuilt, depth + 1));
          continue;
        }
      }
    }
    out.push(node);
  }
  return out;
}

/** Keep only bold/italic/underline/properNoun writing marks; drop font/color/style/strike/code/etc. */
function stripDisallowedWritingMarks(
  node: Record<string, unknown>
): Record<string, unknown> {
  const n = { ...node } as Record<string, unknown> & {
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
    content?: unknown[];
  };
  if (Array.isArray(n.marks) && n.marks.length > 0) {
    const kept = n.marks
      .filter((m) => m?.type && ALLOWED_WRITING_MARKS.has(m.type))
      .map((m) => {
        // Normalize strong/em aliases to bold/italic for TipTap StarterKit.
        if (m.type === "strong") return { ...m, type: "bold" };
        if (m.type === "em") return { ...m, type: "italic" };
        return m;
      });
    if (kept.length) n.marks = kept;
    else delete n.marks;
  }
  if (Array.isArray(n.content)) {
    n.content = n.content.map((child) =>
      stripDisallowedWritingMarks(child as Record<string, unknown>)
    );
  }
  return n;
}

/** Flatten unsupported blocks (headings, lists, etc.) into paragraphs. */
function normalizeBlocksToParagraphs(nodes: unknown[]): ChapterBlockNode[] {
  const out: ChapterBlockNode[] = [];
  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    const n = node as Record<string, unknown> & {
      type?: string;
      attrs?: Record<string, unknown>;
      content?: unknown[];
    };
    const type = typeof n.type === "string" ? n.type : "";

    if (type === "paragraph") {
      out.push(stripDisallowedWritingMarks(n) as ChapterBlockNode);
      continue;
    }

    if (type === "hardBreak") {
      // hardBreak is inline; skip at block level
      continue;
    }

    if (LIST_BLOCK_TYPES.has(type) && Array.isArray(n.content)) {
      out.push(...normalizeBlocksToParagraphs(n.content));
      continue;
    }

    if (BLOCK_TYPES_TO_PARAGRAPH.has(type)) {
      const children = Array.isArray(n.content) ? n.content : [];
      // listItem / blockquote may contain nested paragraphs — flatten those.
      const hasBlockChildren = children.some(
        (c) =>
          c &&
          typeof c === "object" &&
          ["paragraph", "heading", "blockquote", "bulletList", "orderedList", "listItem"].includes(
            String((c as { type?: unknown }).type ?? "")
          )
      );
      if (hasBlockChildren) {
        out.push(...normalizeBlocksToParagraphs(children));
      } else {
        out.push(
          stripDisallowedWritingMarks({
            type: "paragraph",
            attrs: { id: typeof n.attrs?.id === "string" ? n.attrs.id : "" },
            content: children,
          }) as ChapterBlockNode
        );
      }
      continue;
    }

    if (type === "horizontalRule") {
      continue;
    }

    if (type === "image") {
      const attrs = n.attrs && typeof n.attrs === "object" ? n.attrs : {};
      const assetId = typeof attrs.assetId === "string" ? attrs.assetId.trim() : "";
      const srcRaw = typeof attrs.src === "string" ? attrs.src : "";
      // Persist asset references, never giant data URLs in chapter JSON.
      const src = srcRaw.startsWith("data:") ? "" : srcRaw;
      if (assetId || src) {
        out.push({
          type: "image",
          attrs: {
            id: typeof attrs.id === "string" ? attrs.id : "",
            assetId,
            src,
            alt: typeof attrs.alt === "string" ? attrs.alt : "",
          },
        } as ChapterBlockNode);
      }
      continue;
    }

    // Unknown block with content: try to flatten children, else wrap as paragraph.
    if (Array.isArray(n.content) && n.content.length > 0) {
      const nested = normalizeBlocksToParagraphs(n.content);
      if (nested.length > 0) {
        out.push(...nested);
        continue;
      }
    }
  }
  return out.length > 0
    ? out
    : [{ type: "paragraph", attrs: { id: "" }, content: [] }];
}

/** Normalize raw chapter content to a TipTap doc (assigns paragraph ids when missing). */
export function toChapterDoc(raw: unknown): { doc: DocContent; hasText: boolean } {
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      const empty: DocContent = { type: "doc", content: [{ type: "paragraph", attrs: { id: "" }, content: [] }] };
      return { doc: ensureParagraphIds(empty), hasText: false };
    }
    const docFromJson = tryParseStringifiedTipTapDoc(trimmed);
    if (docFromJson) {
      parsed = docFromJson;
    } else if (/^<[a-z!?]/i.test(trimmed)) {
      return { doc: plainTextToChapterDoc(stripHtmlToPlainTextWithParagraphs(trimmed)), hasText: trimmed.length > 0 };
    } else if (containsSerializedTipTapNodes(trimmed)) {
      // Corrupt / unparsable TipTap payload (e.g. bracket-less or truncated node run).
      // Try to salvage the real nodes; only give up (empty) if nothing recovers.
      const ex = extractSerializedTipTapNodes(trimmed);
      if (ex && ex.nodes.length > 0) {
        parsed = { type: "doc", content: coerceToBlockNodes(ex.nodes) };
      } else {
        const empty: DocContent = { type: "doc", content: [{ type: "paragraph", attrs: { id: "" }, content: [] }] };
        return { doc: ensureParagraphIds(empty), hasText: false };
      }
    } else {
      return { doc: plainTextToChapterDoc(trimmed), hasText: trimmed.trim().length > 0 };
    }
  }

  // Recover when a TipTap doc was accidentally stored with JSON as a text node.
  const unwrapped = unwrapNestedTipTapDoc(parsed);
  if (unwrapped) parsed = unwrapped;

  const obj = parsed && typeof parsed === "object" ? (parsed as { type?: string; content?: unknown[] }) : null;
  let contentArray: unknown[] = Array.isArray(obj?.content) ? obj.content : [];
  if (contentArray.length === 0 && Array.isArray(parsed)) contentArray = parsed as unknown[];
  contentArray = recoverNestedJsonBlocks(contentArray);
  const normalizedContent = normalizeBlocksToParagraphs(
    contentArray.length > 0 ? contentArray : [{ type: "paragraph", attrs: { id: "" }, content: [] }]
  );
  const doc: DocContent = {
    type: "doc",
    content: normalizedContent,
  };
  const withIds = ensureParagraphIds(doc);
  return { doc: withIds, hasText: hasAnyText((withIds.content ?? []) as unknown[]) };
}

export function plainTextToChapterDoc(text: string): DocContent {
  const normalized = text.replace(/\r\n/g, "\n").trim();
  if (!normalized) {
    return ensureParagraphIds({ type: "doc", content: [{ type: "paragraph", attrs: { id: "" }, content: [] }] });
  }
  const paragraphs = normalized.split(/\n{2,}/).map((p) => p.replace(/\n/g, " ").trim());
  const content = paragraphs.map((line) => ({
    type: "paragraph" as const,
    attrs: { id: "" },
    content: line ? [{ type: "text" as const, text: line }] : [],
  }));
  return ensureParagraphIds({ type: "doc", content });
}

/** Remove viewer-only marks so persistence matches web (`chapter_comments` owns overlays). */
export function stripCommentOverlayMarksFromJson(doc: DocContent): DocContent {
  function stripNode(node: DocContent | Record<string, unknown>): Record<string, unknown> {
    const n = { ...node } as Record<string, unknown> & {
      marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
      content?: unknown[];
    };
    if (Array.isArray(n.marks) && n.marks.length > 0) {
      const kept = n.marks.filter((m) => m?.type !== "comment" && m?.type !== "highlight");
      if (kept.length) n.marks = kept;
      else delete n.marks;
    }
    if (Array.isArray(n.content)) {
      n.content = n.content.map((child) => stripNode(child as Record<string, unknown>));
    }
    return n;
  }
  return stripNode(doc) as DocContent;
}

/** Canonical string form for chapter.content in desktop-v2 (TipTap JSON with paragraph ids). */
export function serializeChapterContent(raw: unknown): string {
  const { doc } = toChapterDoc(raw);
  const stripped = stripCommentOverlayMarksFromJson(doc);
  return JSON.stringify(stripped);
}

/** Stable fingerprint for comparing chapter bodies (ignores comment overlay marks). */
export function chapterContentFingerprint(title: string, content: unknown): string {
  const { doc } = toChapterDoc(content);
  const stripped = stripCommentOverlayMarksFromJson(doc);
  return `${title}\n${JSON.stringify(sortKeysDeep(stripped))}`;
}

export function chapterContentChanged(before: unknown, after: unknown, title = ""): boolean {
  return chapterContentFingerprint(title, before) !== chapterContentFingerprint(title, after);
}

export function normalizeChapterContentForStorage(value: unknown): string {
  return serializeChapterContent(value);
}

function stripHtmlToPlainTextWithParagraphs(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\s*\/p\s*>\s*<\s*p[^>]*>/gi, "\n\n")
    .replace(/<\s*\/(?:p|h[1-6]|li)\s*>/gi, "\n\n")
    .replace(/<\s*(?:p|h[1-6]|li)[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\u00A0/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Plain text extraction for AI prompts, word counts, publish pagination, and variation diffs. */
export function chapterContentToPlainText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    const plain = tipTapJsonToPlainText(value).trim();
    // Recover when a TipTap doc was accidentally stored with JSON as a text node.
    if (looksLikeTipTapJsonString(plain)) {
      const nested = tryParseStringifiedTipTapDoc(plain);
      if (nested) return tipTapJsonToPlainText(nested).trim();
      return "";
    }
    return plain;
  }
  if (typeof value !== "string") return String(value);

  const doc = tryParseStringifiedTipTapDoc(value);
  if (doc) {
    const plain = tipTapJsonToPlainText(doc).trim();
    if (looksLikeTipTapJsonString(plain)) {
      const nested = tryParseStringifiedTipTapDoc(plain);
      if (nested) return tipTapJsonToPlainText(nested).trim();
      return "";
    }
    return plain;
  }

  const trimmed = value.trim();
  if (!trimmed) return "";

  // Never paginate / export raw TipTap JSON as prose when parse failed.
  if (looksLikeTipTapJsonString(trimmed)) {
    return "";
  }

  if (/^<[a-z!?]/i.test(trimmed)) return stripHtmlToPlainTextWithParagraphs(trimmed);
  return trimmed.replace(/\r\n/g, "\n").replace(/\u00A0/g, " ");
}

/** Rich paragraphs (with bold/italic/underline runs) for publish pagination. */
export function chapterContentToRichParagraphs(value: unknown): RichParagraph[] {
  if (value == null) return [];

  if (typeof value === "object") {
    const doc = unwrapNestedTipTapDoc(value) ?? (value as Record<string, unknown>);
    const paras = tipTapJsonToRichParagraphs(doc);
    if (paras.length) return paras;
    const plain = tipTapJsonToPlainText(doc).trim();
    return plain ? plainTextToRichParagraphs(plain) : [];
  }

  if (typeof value !== "string") return plainTextToRichParagraphs(String(value));

  const doc = unwrapNestedTipTapDoc(value);
  if (doc) {
    const paras = tipTapJsonToRichParagraphs(doc);
    if (paras.length) return paras;
  }

  const trimmed = value.trim();
  if (!trimmed) return [];
  if (looksLikeTipTapJsonString(trimmed)) return [];
  if (/^<[a-z!?]/i.test(trimmed)) {
    return plainTextToRichParagraphs(stripHtmlToPlainTextWithParagraphs(trimmed));
  }
  return plainTextToRichParagraphs(trimmed.replace(/\r\n/g, "\n").replace(/\u00A0/g, " "));
}
